import { useEffect, useState } from "react";
import { FaSearch } from "react-icons/fa";
import { MdDeleteOutline, MdOutlineEdit } from "react-icons/md";
import DeleteConfirmationModal from "../../../DeleteConfirmationModal/DeleteConfirmationModal";
import LoaderSpiner from "../../../LoaderSpiner";
import styles from "./ScriptandApi.module.css";

const PromptManager = () => {
  const [prompts, setPrompts] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [promptToDelete, setPromptToDelete] = useState(null);
  const [type, setType] = useState("outbound");
  const [promptName, setPromptName] = useState("");
  const [promptDescription, setPromptDescription] = useState("");
  const [generatedPrompt, setGeneratedPrompt] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [saveError, setSaveError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [filteredPrompts, setFilteredPrompts] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [locationsInput, setLocationsInput] = useState("");
  const [locationError, setLocationError] = useState("");
  const [userRole, setUserRole] = useState("");

  // Initialize user role and set type based on route
  useEffect(() => {
    const role = localStorage.getItem("role");
    setUserRole(
      role ? role.charAt(0).toUpperCase() + role.slice(1).toLowerCase() : ""
    );
    const pathname = window.location.pathname;
    if (pathname.includes("/outbound/scripts")) {
      setType("outbound");
    } else if (pathname.includes("/inbound/scripts")) {
      setType("inbound");
    }
  }, []);

  // Fetch prompts
  useEffect(() => {
    const fetchPrompts = async () => {
      const token = localStorage.getItem("token");
      const userId = localStorage.getItem("userId");
      if (!token || !userId) {
        setSaveError(
          "Authentication token or user ID is missing. Please log in."
        );
        return;
      }
      let API_URL = `https://prod.toxboxai.com/api/prompts/get-prompts-by-user/${userId}?type=${type}`;
      if (userRole === "Participant") {
        API_URL = `https://prod.toxboxai.com/api/participant/assign-prompts-by-salesperson/${userId}/${type}`;
      }
      try {
        setIsLoading(true);
        const response = await fetch(API_URL, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok)
          throw new Error(`Failed to fetch prompts: ${response.status}`);
        const data = await response.json();
        setPrompts(Array.isArray(data.prompts) ? data.prompts : []);
      } catch (error) {
        setSaveError(`Failed to fetch prompts: ${error.message}`);
      } finally {
        setIsLoading(false);
      }
    };
    fetchPrompts();
  }, [type, userRole]);

  // Filter prompts
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setFilteredPrompts(
        search.trim() === ""
          ? prompts
          : prompts.filter(
            (prompt) =>
              prompt.promptName
                ?.toLowerCase()
                .includes(search.toLowerCase()) ||
              prompt.promptDescription
                ?.toLowerCase()
                .includes(search.toLowerCase()) ||
              prompt.generatedPrompt
                ?.toLowerCase()
                .includes(search.toLowerCase())
          )
      );
    }, 300);
    return () => clearTimeout(debounceTimer);
  }, [search, prompts]);

  const truncateWords = (text, limit) => {
    const words = text?.split(" ") || [];
    return words.length > limit
      ? words.slice(0, limit).join(" ") + "..."
      : text || "";
  };

  const truncateString = (text, limit) =>
    text?.length > limit ? text.slice(0, limit) + "..." : text || "";

  const handleSave = async () => {
    setSaveError("");
    setSuccessMessage("");
    try {
      if (!promptName.trim()) throw new Error("Prompt name is required");
      if (!promptDescription.trim())
        throw new Error("Prompt description is required");
      const token = localStorage.getItem("token");
      if (!token)
        throw new Error("Authentication token missing. Please log in.");
      const API_URL = editingId
        ? `https://prod.toxboxai.com/api/prompts/${editingId}`
        : "https://prod.toxboxai.com/api/prompts/create-prompt";
      const data = {
        promptName,
        promptDescription,
        locations: selectedLocations.length > 0 ? selectedLocations : null,
        type,
      };
      const response = await fetch(API_URL, {
        method: editingId ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || `Failed to save prompt: HTTP ${response.status}`
        );
      }
      const responseData = await response.json();
      setGeneratedPrompt(responseData.prompt?.generatedPrompt || "");
      setSuccessMessage("Prompt saved successfully!");
      const fetchResponse = await fetch(
        `https://prod.toxboxai.com/api/prompts/get-prompts-by-user/${localStorage.getItem(
          "userId"
        )}?type=${type}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const fetchData = await fetchResponse.json();
      setPrompts(Array.isArray(fetchData.prompts) ? fetchData.prompts : []);
      setIsModalOpen(false);
      resetForm();
    } catch (error) {
      setSaveError(error.message || "Failed to save prompt.");
    }
  };

  const handleEdit = async (id) => {
    const token = localStorage.getItem("token");
    if (!token) {
      setSaveError("Authentication token missing. Please log in.");
      return;
    }
    try {
      const response = await fetch(
        `https://prod.toxboxai.com/api/prompts/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!response.ok)
        throw new Error(`Failed to fetch prompt: ${response.status}`);
      const data = await response.json();
      const prompt = data.prompt;
      setPromptName(prompt.promptName || "");
      setPromptDescription(prompt.promptDescription || "");
      setGeneratedPrompt(prompt.generatedPrompt || "");
      setSelectedLocations(
        Array.isArray(prompt.locations) ? prompt.locations : []
      );
      setLocationsInput(
        Array.isArray(prompt.locations) ? prompt.locations.join(", ") : ""
      );
      setType(prompt.type || "outbound");
      setEditingId(id);
      setIsModalOpen(true);
      setSaveError("");
      setSuccessMessage("");
    } catch (error) {
      setSaveError(`Failed to fetch prompt for editing: ${error.message}`);
    }
  };

  const handleDelete = (id) => {
    const prompt = prompts.find((p) => p._id === id);
    setPromptToDelete(prompt);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!promptToDelete) return;
    const token = localStorage.getItem("token");
    if (!token) {
      setSaveError("Authentication token missing. Please log in.");
      setIsDeleteModalOpen(false);
      setPromptToDelete(null);
      return;
    }
    try {
      const response = await fetch(
        `https://prod.toxboxai.com/api/prompts/${promptToDelete._id}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!response.ok)
        throw new Error(`Failed to delete prompt: ${response.status}`);
      const fetchResponse = await fetch(
        `https://prod.toxboxai.com/api/prompts/get-prompts-by-user/${localStorage.getItem(
          "userId"
        )}?type=${type}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const fetchData = await fetchResponse.json();
      setPrompts(Array.isArray(fetchData.prompts) ? fetchData.prompts : []);
      setIsDeleteModalOpen(false);
      setPromptToDelete(null);
      setSuccessMessage("Prompt deleted successfully!");
    } catch (error) {
      setSaveError("Failed to delete prompt.");
      setIsDeleteModalOpen(false);
      setPromptToDelete(null);
    }
  };

  const resetForm = () => {
    setPromptName("");
    setPromptDescription("");
    setGeneratedPrompt("");
    setEditingId(null);
    setSelectedLocations([]);
    setLocationsInput("");
    setType("outbound");
    setSaveError("");
    setSuccessMessage("");
  };

  const formatLocation = (location) => {
    if (!location) return "";
    return (
      location.trim().charAt(0).toUpperCase() +
      location.trim().slice(1).toLowerCase()
    );
  };

  const handleLocationsInputChange = (e) => {
    const input = e.target.value;
    setLocationsInput(input);
    const locationsArray = input
      .split(",")
      .map((loc) => formatLocation(loc))
      .filter((loc) => loc !== "");
    setSelectedLocations(locationsArray);
  };

  return (
    <div className={`${styles.container} ${styles[type]}`}>
      <div className={styles.TopContainer}>
        <span className={styles.SearchInputIcon}>
          <FaSearch />
        </span>
        <input
          className={styles.SearchInput}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by prompt name, description, or generated prompt"
        />
        {/* {userRole !== "Participant" && (
          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            className={styles.typeSelect}
          >
            <option value="inbound">Inbound</option>
            <option value="outbound">Outbound</option>
          </select>
        )} */}
        {userRole !== "Participant" && (
          <button
            className={styles.addButton}
            onClick={() => {
              setIsModalOpen(true);
              resetForm();
            }}
          >
            Add Prompt
          </button>
        )}
      </div>

      {/* {userRole !== "Participant" && (
        <select
          value={type}
          onChange={(e) => setType(e.target.value)}
          className={styles.typeSelect}
        >
          <option value="inbound">Inbound</option>
          <option value="outbound">Outbound</option>
        </select>
      )} */}
      {isModalOpen && (
        <div className={styles.modal}>
          <div
            className={styles.modalOverlay}
            onClick={() => setIsModalOpen(false)}
          />
          <div className={styles.modalContent}>
            <h2 className={styles.heading}>
              {editingId ? "Edit Prompt" : "Create Prompt"}
            </h2>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSave();
              }}
            >
              <input
                type="text"
                value={promptName}
                onChange={(e) => setPromptName(e.target.value)}
                placeholder="Enter prompt name"
                className={styles.input}
                required
              />
              {/* {userRole !== "Participant" && (
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  className={styles.input}
                >
                  <option value="inbound">Inbound</option>
                  <option value="outbound">Outbound</option>
                </select>
              )} */}
              <input
                type="text"
                value={locationsInput}
                onChange={handleLocationsInputChange}
                placeholder="Enter locations (e.g., New york, San francisco)"
                className={styles.input}
              />
              <textarea
                value={promptDescription}
                onChange={(e) => setPromptDescription(e.target.value)}
                placeholder="Enter prompt description"
                rows={10}
                className={styles.textarea}
                required
              />
              {saveError && <p className={styles.error}>{saveError}</p>}
              {successMessage && (
                <p className={styles.success}>{successMessage}</p>
              )}
              {locationError && <p className={styles.error}>{locationError}</p>}
              <div className={styles.modalButtons}>
                <button type="submit" className={styles.saveButton}>
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsModalOpen(false);
                    resetForm();
                  }}
                  className={styles.cancelButton}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setPromptToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title={promptToDelete?.promptName || "Untitled"}
      />
      <div className={styles.cardContainer}>
        {isLoading ? (
          <LoaderSpiner />
        ) : filteredPrompts.length > 0 ? (
          filteredPrompts.map((prompt) => (
            <div key={prompt._id} className={styles.card}>
              <h3>{truncateString(prompt.promptName || "Untitled", 30)}</h3>
              <p>
                <strong>Description:</strong>{" "}
                {truncateWords(
                  prompt.promptDescription || "No Description",
                  10
                )}
              </p>
              <p>
                <strong>Generated Prompt:</strong>{" "}
                {truncateWords(prompt.generatedPrompt || "Not generated", 10)}
              </p>
              <p className={styles.cardLocations}>
                <strong>Locations:</strong>{" "}
                {truncateWords(
                  Array.isArray(prompt.locations) && prompt.locations.length > 0
                    ? prompt.locations.join(", ")
                    : "No locations",
                  10
                )}
              </p>
              {userRole !== "Participant" && (
                <div className={styles.cardButtons}>
                  <button
                    onClick={() => handleEdit(prompt._id)}
                    className={styles.editButton}
                    aria-label="Edit prompt"
                  >
                    <MdOutlineEdit />
                  </button>
                  <button
                    onClick={() => handleDelete(prompt._id)}
                    className={styles.deleteButton}
                    aria-label="Delete prompt"
                  >
                    <MdDeleteOutline />
                  </button>
                </div>
              )}
            </div>
          ))
        ) : (
          <p>No prompts found.</p>
        )}
      </div>
    </div>
  );
};

export default PromptManager;
